/**
 * Create new meal route
 * Organizer-only page for creating new meals
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useCreateMeal } from '@/hooks/useMeals';
import { MealForm, type MealFormValues } from '@/components/meals/MealForm';
import { useState } from 'react';
import { requireOrganizer } from '@/lib/auth/routeGuards';

export const Route = createFileRoute('/meals/new')({
  beforeLoad: requireOrganizer,
  component: NewMealComponent,
});

function NewMealComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMealMutation = useCreateMeal();
  const [successMessage, setSuccessMessage] = useState(false);

  const handleSubmit = async (values: MealFormValues) => {
    try {
      const newMeal = await createMealMutation.mutateAsync({
        name: values.name,
        tags: values.tags,
        repeatIntervalWeeks: values.repeatIntervalWeeks,
      });

      // Show success message briefly
      setSuccessMessage(true);

      // Navigate to meal detail page
      setTimeout(() => {
        navigate({ to: `/meals/${newMeal.id}` });
      }, 500);
    } catch (error) {
      // Error is handled by the mutation
      console.error('Failed to create meal:', error);
    }
  };

  const handleCancel = () => {
    navigate({ to: '/meals' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate({ to: '/meals' })}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-4"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('meals.backToMeals')}
          </button>

          <h1 className="text-3xl font-bold text-gray-900">{t('meals.createMealTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('meals.createMealDescription')}
          </p>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="rounded-md bg-green-50 p-4 mb-6" role="alert">
            <div className="flex">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {t('meals.mealCreatedSuccess')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {createMealMutation.isError && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('meals.errorCreatingMeal')}</h3>
                <div className="mt-2 text-sm text-red-700">
                  {createMealMutation.error instanceof Error
                    ? createMealMutation.error.message
                    : t('meals.createMealFailed')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <MealForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={createMealMutation.isPending}
            submitText={t('meals.createMealButton')}
          />
        </div>

        {/* Helper info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">{t('meals.tipsForAdding')}</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('meals.tipDescriptiveNames')}</li>
                  <li>{t('meals.tipVisibleTags')}</li>
                  <li>{t('meals.tipHiddenTags')}</li>
                  <li>{t('meals.tipCommonTags')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
